/**
 * Guest Briefing Prompts
 * 
 * Functions for generating one-page briefing documents for podcast hosts.
 * Uses canonical field definitions via context parameter.
 */

import aiService from '../../services/aiService.js';
import {
    COMMON_GUARDRAILS,
    COT_INSTRUCTIONS,
    buildStructuredPrompt
} from '../../utils/promptTemplates.js';

/**
 * Generate Guest Briefing
 * Creates a one-page briefing document for the host
 * 
 * @param {object} context - { guestName, guestBio, focus }
 * @returns {Promise<string>} - Markdown briefing
 */
export const generateGuestBriefing = async (context) => {
    const focus = context.focus || 'General Overview';

    const sections = {
        role: "You are an Executive Producer. Prepare a high-level briefing document for the host.",

        context: `
**GUEST:** ${context.guestName}
**RAW DOSSIER:**
${context.guestBio}
        `,

        task: `
Synthesize the raw data into a clean, skimmable 1-page briefing.
Primary Focus: **${focus}**.
        `,

        instructions: `
${COT_INSTRUCTIONS}

Focus on:
1. **Who is this person?** (The "Elevator Pitch" version).
2. **Why are they relevant NOW?** (Timeliness).
3. **The "Unfair Advantage":** What do they know that no one else does?
4. **Three "Talkable" topics:** Specific stories mentioned in the bio, emphasizing ${focus}.
        `,

        format: `
**OUTPUT FORMAT:**

# 🎙️ Guest Briefing: ${context.guestName}
**(Focus: ${focus})**

## ⚡ Fast Facts
* **Role:** [Current Title]
* **Known For:** [Main claim to fame]

## 🧠 The "Unfair Advantage"
> [2-3 sentences on their unique expertise]

## 🗣️ Talkable Topics
* **[Topic 1]:** [Context]
* **[Topic 2]:** [Context]

## ⚠️ Do's & Don'ts
* [Optional: Sensitive topics or specific phrasing tips]
        `,

        guardrails: COMMON_GUARDRAILS
    };

    const prompt = buildStructuredPrompt(sections);
    return await aiService.generateContent(prompt, 'PLANNING');
};

const guestBriefingExports = {
    generateGuestBriefing
};

export default guestBriefingExports;
